// stores/auth.js
import { writable, derived } from 'svelte/store';
import { authService } from '../services/api';
import { demoMode, DEMO_USER } from './demoMode';

function createAuthStore() {
  const initialState = {
    isAuthenticated: false,
    user: null,
    isLoading: false,
    error: null,
    initialized: false
  };

  const { subscribe, set, update } = writable(initialState);

  // Initialize store with localStorage state
  function init() {
    // Check for demo mode first
    if (demoMode.isEnabled()) {
      // Auto-setup demo mode
      localStorage.setItem('accessToken', 'demo-token');
      localStorage.setItem('username', DEMO_USER.username);

      update(state => ({
        ...state,
        isAuthenticated: true,
        user: {
          username: DEMO_USER.username,
          isDemo: true
        },
        initialized: true
      }));
      return;
    }

    const authState = authService.checkAuth();
    update(state => ({
      ...state,
      isAuthenticated: authState.isAuthenticated,
      user: authState.username ? { username: authState.username } : null,
      initialized: true
    }));
  }

  // Listen for auth changes
  let unsubscribe;
  if (typeof window !== 'undefined') {
    unsubscribe = authService.onAuthChange((isAuthenticated, isSessionExpired) => {
      // Get storage based on remember me preference
      const rememberMe = localStorage.getItem('rememberMe') === 'true';
      const storage = rememberMe ? localStorage : sessionStorage;
      
      // Update store state
      update(state => ({
        ...state,
        isAuthenticated,
        isSessionExpired: isSessionExpired || false,
        user: isAuthenticated ? { 
          username: storage.getItem('username') || localStorage.getItem('username'),
          rememberMe
        } : null
      }));
      
      // Handle session expiration by showing a notification
      if (isSessionExpired) {
        // Show a notification that the session has expired
        const event = new CustomEvent('app:notification', {
          detail: {
            type: 'warning',
            message: 'Your session has expired. Please log in again.',
            duration: 5000
          }
        });
        window.dispatchEvent(event);
      }
    });
  }

  return {
    subscribe,
    
    async login(username, password, rememberMe = false) {
      if (!username?.trim() || !password?.trim()) {
        throw new Error('Username and password are required');
      }

      update(state => ({ ...state, isLoading: true, error: null }));
      
      try {
        // Pass rememberMe to auth service
        const response = await authService.login(username, password, rememberMe);
        
        update(state => ({
          ...state,
          isAuthenticated: true,
          user: { 
            username, 
            rememberMe, 
            ...response 
          },
          isLoading: false
        }));
        
        return response;
      } catch (error) {
        update(state => ({
          ...state,
          isAuthenticated: false,
          user: null,
          error: error.message,
          isLoading: false
        }));
        throw error;
      }
    },

    async register(username, password) {
      if (!username?.trim() || !password?.trim()) {
        throw new Error('Username and password are required');
      }

      update(state => ({ ...state, isLoading: true, error: null }));
      
      try {
        const response = await authService.register(username, password);
        update(state => ({
          ...state,
          isAuthenticated: true,
          user: { username, ...response },
          isLoading: false
        }));
        return response;
      } catch (error) {
        update(state => ({
          ...state,
          error: error.message,
          isLoading: false
        }));
        throw error;
      }
    },

    // Demo mode login - no backend required
    async demoLogin() {
      update(state => ({ ...state, isLoading: true, error: null }));

      try {
        // Enable demo mode
        demoMode.enable();

        // Set demo user state
        update(state => ({
          ...state,
          isAuthenticated: true,
          user: {
            username: DEMO_USER.username,
            isDemo: true
          },
          isLoading: false
        }));

        // Set a fake token so auth checks pass
        localStorage.setItem('accessToken', 'demo-token');
        localStorage.setItem('username', DEMO_USER.username);

        return { success: true, demo: true };
      } catch (error) {
        update(state => ({
          ...state,
          error: error.message,
          isLoading: false
        }));
        throw error;
      }
    },

    async logout() {
      update(state => ({ ...state, isLoading: true, error: null }));

      try {
        // Disable demo mode if it was enabled
        demoMode.disable();

        await authService.logout();
        set({
          ...initialState,
          initialized: true
        });
      } catch (error) {
        update(state => ({
          ...state,
          error: error.message,
          isLoading: false
        }));
        throw error;
      }
    },

    checkAuth() {
      // Check if we're in demo mode first
      if (demoMode.isEnabled()) {
        // Auto-setup demo mode tokens
        localStorage.setItem('accessToken', 'demo-token');
        localStorage.setItem('username', DEMO_USER.username);

        update(state => ({
          ...state,
          isAuthenticated: true,
          user: {
            username: DEMO_USER.username,
            isDemo: true
          },
          initialized: true
        }));
        return { isAuthenticated: true, username: DEMO_USER.username, isDemo: true };
      }

      const authState = authService.checkAuth();
      update(state => ({
        ...state,
        isAuthenticated: authState.isAuthenticated,
        user: authState.username ? {
          username: authState.username,
          rememberMe: authState.rememberMe || false
        } : null,
        initialized: true
      }));
      return authState;
    },

    clearError() {
      update(state => ({ ...state, error: null }));
    },

    reset() {
      set({
        ...initialState,
        initialized: true
      });
    },

    // Initialize the store
    init
  };
}

export const auth = createAuthStore();

// Initialize auth store
if (typeof window !== 'undefined') {
  auth.init();
}